import { ITelemetryEvent } from '@mecontrol/common';
import { ME, createError } from '../../utilities';
import Promise from 'promise-polyfill';
import { TrustedTypesWindow } from 'trusted-types/lib';

let _w: any = window;
let _appInsights: any;
/**
 * Maximum retries to execute the downloaded 1ds minified library
 */
const MAX_RETRIES = 3;

// Performance.now() and Date.now() Polyfills
if ('performance' in window == false) {
    _w.performance = {};
}

Date.now =
    Date.now ||
    function (): number {
        return new Date().getTime();
    };

if ('now' in window.performance === false) {
    let nowOffset = Date.now();

    if (
        window.performance.timing &&
        window.performance.timing.navigationStart
    ) {
        nowOffset = window.performance.timing.navigationStart;
    }

    window.performance.now = function (): number {
        return Date.now() - nowOffset;
    };
}

let eventsQueue: ITelemetryEvent[] = [];

/**
 * Send events to 1DS
 * @param events Array of events to send
 */
export function writeEvent(events: ITelemetryEvent[]): void {
    eventsQueue = eventsQueue.concat(events);
    flushEvents();
}

export function flushEvents(): void {
    if (_appInsights) {
        eventsQueue.forEach(e => {
            _appInsights.track(getEventData(e));
        });

        eventsQueue = [];
    }
}

/**
 * Take in a telemetry event for Vortex and convert it into the final shape that
 * Vortex events have, so that JSLL can process it
 * @param event Event to convert
 */
function getEventData(event: ITelemetryEvent): any {
    return {
        name: event.partA.name,
        data: {
            ...event.partA,
            data: {
                baseData: event.partB && { ...event.partB },
                ...event.partC
            }
        }
    };
}

const ttWindow = window as unknown as TrustedTypesWindow;
const trustedTypesLibrary = typeof ttWindow.trustedTypes !== "undefined" &&
    typeof ttWindow.trustedTypes.createPolicy !== "undefined"
    ? ttWindow.trustedTypes
    : { createPolicy: (_name: string, rules: any) => rules }; // Tinyfill for unsupported browsers

const ScriptURLPolicy1DS = trustedTypesLibrary.createPolicy('1DSScriptURL', {
    createScriptURL: (url: string): string => {
        if (url) {
            if (new URL(url).origin === "https://js.monitor.azure.com") {
                return url;
            } else {
                throw createError("1DS Script url is not trusted. Does not match the expected origin.");
            }
        } else {
            throw createError("1DS Script url is not trusted. Expected url is not defined.");
        }
    }
});

export function initialize1DS(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        if (_appInsights) {
            resolve();
            return;
        }

        /** Iterations to instantiate AppInsights */
        let i = 0;
        const url = ME.Config.oneDSUrl;
        const head = document.getElementsByTagName('head')[0];
        let script: HTMLScriptElement | undefined =
            document.createElement('script');
        script.async = true;
        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        try {
            script.src = ScriptURLPolicy1DS.createScriptURL(url) as unknown as string;
        } catch(error) {
            cleanup();
            reject(error);
        }

        const timeoutId = setTimeout(onScriptTimeout, ME.Config.timeoutMs);

        // IE9 - IE8 and below require using 'onreadystatechange'
        script.onload = onScriptComplete;
        script.onerror = onScriptError;

        script.crossOrigin = 'anonymous';
        head.appendChild(script);

        function cleanup(): void {
            clearTimeout(timeoutId);
            if (script) {
                // Avoid memory leaks in IE
                script.onerror = script.onload = undefined as any;
                script = undefined;
            }
        }

        function onScriptError(event: Event | string): void {
            cleanup();
            const errorMessage = 'oneDsMeControl failed to download/parse/compile';
            reject(createError(`${errorMessage}: ${url}`, true));
        }

        function onScriptTimeout(): void {
            cleanup();
            const errorMessage = 'oneDsMeControl download timed out';
            reject(createError(`${errorMessage}: ${url}`, true));
        }

        function onScriptComplete(event: Event | string): void {
            if (i < MAX_RETRIES) {
                /**
                 * To queue onto the event loop
                 */
                setTimeout(initializeAppInsightsCore, 0);
            } else {
                reject('oneDsMeControl did not instantiate');
            }
        }
        function initializeAppInsightsCore() {
            i++;
            try {
                if (!_w.oneDsMeControl) throw 'oneDsMeControl variable is undefined';
                const appInsightsCore = new _w.oneDsMeControl.AppInsightsCore();
                const postChannel = new _w.oneDsMeControl.PostChannel();
                const propertiesPlugin = new _w.oneDsMeControl.PropertiesPlugin();

                const postChannelConfig = {
                    eventsLimitInMem: 50
                };
                const propertiesPluginConfig = {
                    gpcDataSharingOptIn: false,
                    hashIdentifiers: true
                };
                let coreConfig: any = {
                    instrumentationKey: ME.Config.instKey,
                    extensions: [ postChannel, propertiesPlugin],
                    extensionConfig: [],
                    cookieCfg: {
                        ignoreCookies: [ "ANON", "MicrosoftApplicationsTelemetryDeviceId" ]
                    },
                };
                coreConfig.extensionConfig[postChannel.identifier] = postChannelConfig;
                coreConfig.extensionConfig[propertiesPlugin.identifier] = propertiesPluginConfig;

                //Initialize SDK
                appInsightsCore.initialize(coreConfig, []);
                _appInsights = appInsightsCore;

                cleanup();
                resolve();
            } catch {
                onScriptComplete('');
            }
        }
    });
}
